import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import {
  AutoComplete,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DraftFileUploader,
  FormControlMessage,
  Loader,
  TextInput,
} from "fe-shared/src/components";
import { CheckIcon } from "fe-shared/src/components/Icons";
import useValidation from "fe-shared/src/hooks/useValidation";
import { getDateObject, getDateString } from "fe-shared/src/helpers/dates";
import useParticipantSearch from "fe-shared/src/hooks/useParticipantSearch";
import {
  BusinessRole,
  MaterialUnit,
  TireMaterialConfig,
} from "fe-shared/src/types/domain";
import { getMergedContent } from "fe-shared/src/helpers/validation";
import { Retreader } from "fe-shared-transactions/src/types";
import { CollectionInfoTooltip } from "fe-shared-transactions/src/components";

import { RetreadingTransactionFormContent } from "../../types";
import Period from "./components/Period";
import RecoveryMaterialsTable from "./components/RecoveryMaterialsTable";
import MaterialsTable from "./components/MaterialsTable";
import { convertTransactionToForm } from "./modules/helpers";
import {
  errorMessages,
  initialContent,
  getValidationConfig,
} from "./modules/validation";

import styles from "./RetreadingTransactionFormDialog.module.scss";

export interface Props {
  title: string;
  isActive: boolean;
  transaction?: Retreader;
  materials: TireMaterialConfig[];
  onClose: () => void;
  onSubmit: (values: RetreadingTransactionFormContent) => void;
  isLoading?: boolean;
}

const RetreadingTransactionFormDialog = ({
  title,
  isActive,
  materials,
  onClose,
  onSubmit,
  transaction,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const {
    searchText,
    setParticipantSearchText,
    participants: {
      data: participantsList,
      isLoading: isParticipantListLoading,
    },
  } = useParticipantSearch(BusinessRole.Retreader);
  const maxProductionReportDateRef = useRef(new Date());

  const mergedContent = useMemo(() => {
    const emptyContent = initialContent;

    if (!transaction) return emptyContent;

    return getMergedContent(
      emptyContent,
      convertTransactionToForm(transaction)
    );
  }, [transaction]);

  const {
    errors,
    values,
    isValid,
    validateAll,
    validateValue,
    updateValue,
  } = useValidation(getValidationConfig(), mergedContent, errorMessages);

  const collectionEstimationValues = useMemo(() => {
    return materials.reduce(
      (acc: Record<string, number>, material: TireMaterialConfig) => {
        const materialInputValue =
          values.collectionInfoMaterials[material.code] || 0;

        if (values.isKnownRetreaderTiresActualWeight) {
          acc[material.code] = Math.ceil(
            materialInputValue / material.standardWeight
          );
        } else {
          acc[material.code] = material.standardWeight * materialInputValue;
        }

        return acc;
      },
      {}
    );
  }, [
    materials,
    values.collectionInfoMaterials,
    values.isKnownRetreaderTiresActualWeight,
  ]);
  const retreadingEstimationUnits = useMemo(() => {
    return values.isKnownRetreaderTiresActualWeight
      ? collectionEstimationValues
      : values.collectionInfoMaterials;
  }, [
    collectionEstimationValues,
    values.collectionInfoMaterials,
    values.isKnownRetreaderTiresActualWeight,
  ]);
  const collectionWeightValues = useMemo(() => {
    return values.isKnownRetreaderTiresActualWeight
      ? values.collectionInfoMaterials
      : collectionEstimationValues;
  }, [
    collectionEstimationValues,
    values.collectionInfoMaterials,
    values.isKnownRetreaderTiresActualWeight,
  ]);
  const totalCollectionWeight = useMemo(() => {
    return Object.values(collectionWeightValues).reduce(
      (a: number, b: number) => +a + +b,
      0
    );
  }, [collectionWeightValues]);
  const totalRetreadingWeight = useMemo(() => {
    return Object.values(values.retreadRepairInfoMaterials).reduce(
      (a: number, b: number) => +a + +b,
      0
    );
  }, [values.retreadRepairInfoMaterials]);
  const recoveryGain = useMemo(
    () => totalRetreadingWeight - totalCollectionWeight,
    [totalCollectionWeight, totalRetreadingWeight]
  );
  const collectionMaterialsErrors = useMemo(() => {
    return materials.reduce(
      (acc: Record<string, boolean>, { code }: TireMaterialConfig) => {
        const collectionValue = values.collectionInfoMaterials[code];
        const repairValue = values.retreadRepairInfoMaterials[code];

        acc[code] = repairValue > 0 && !collectionValue;

        return acc;
      },
      {}
    );
  }, [
    materials,
    values.collectionInfoMaterials,
    values.retreadRepairInfoMaterials,
  ]);
  const retreadRepairMaterialsErrors = useMemo(() => {
    return materials.reduce(
      (acc: Record<string, boolean>, { code }: TireMaterialConfig) => {
        const collectionValue = +collectionWeightValues[code];
        const repairValue = values.retreadRepairInfoMaterials[code];

        acc[code] =
          (collectionValue > 0 && !repairValue) ||
          (collectionValue >= repairValue && repairValue > 0);

        return acc;
      },
      {}
    );
  }, [collectionWeightValues, materials, values.retreadRepairInfoMaterials]);
  const hasMaterialError: boolean = useMemo(() => {
    return materials.some(
      ({ code }: TireMaterialConfig) =>
        collectionMaterialsErrors[code] || retreadRepairMaterialsErrors[code]
    );
  }, [collectionMaterialsErrors, materials, retreadRepairMaterialsErrors]);
  const isSubmitDisabled = useMemo(
    () => !!Object.keys(errors).length || isLoading || hasMaterialError,
    [errors, isLoading, hasMaterialError]
  );

  const onIsKnownActualWeightCheckboxClick = () => {
    updateValue(
      "isKnownRetreaderTiresActualWeight",
      !values.isKnownRetreaderTiresActualWeight
    );
    updateValue("collectionInfoMaterials", {});
  };

  const onSubmitButtonClick = useCallback(() => {
    validateAll();
    if (isValid) {
      onSubmit(values);
    }
  }, [values, isValid, validateAll, onSubmit]);

  const actions: React.ReactNode[] = [
    <Button
      key="1"
      variant="filled"
      onClick={onSubmitButtonClick}
      iconComponent={CheckIcon}
      disabled={isSubmitDisabled}
      id="create-retreading-transaction-dialog-save-btn"
    >
      {t("submit")}
    </Button>,
    <Button
      key="2"
      onClick={onClose}
      variant="outlined"
      id="create-retreading-transaction-dialog-cancel-btn"
    >
      {t("cancel")}
    </Button>,
  ];

  return (
    <Dialog
      title={title}
      modal
      width={1028}
      isActive={isActive}
      onClose={onClose}
      actions={actions}
    >
      {isLoading && <Loader isBlocking />}
      <div className={styles.dialogContent}>
        <div className={styles.dialogCol6}>
          <AutoComplete
            id="company-select-input"
            title={t(
              "transactions.grid.createRetreadingTransaction.form.retreader"
            )}
            isEmptyStateVisible={
              !!searchText.length && !isParticipantListLoading
            }
            placeholder={t(
              "transactions.grid.createRetreadingTransaction.form.retreaderPlaceholder"
            )}
            value={
              values.companyId
                ? {
                    value: values.companyId,
                    label: values.companyName as string,
                  }
                : undefined
            }
            options={participantsList}
            isLoading={isParticipantListLoading}
            isError={!!errors.companyId}
            errorText={errors.companyId}
            onBlur={() => validateValue("companyId")}
            onChange={(event, value, label) => {
              updateValue("companyId", value, true);
              updateValue("companyName", label, true);
            }}
            onTextChange={(e, v) => {
              setParticipantSearchText(v.trim().toLowerCase());
            }}
            inputClassName={styles.formField}
            required
            disabled={!!transaction}
          />

          <Period errors={errors} values={values} updateValue={updateValue} />

          <Checkbox
            id="know-actual-weight-checkbox"
            title={t(
              "transactions.grid.createRetreadingTransaction.form.isKnownActualWeightCheckbox"
            )}
            onChange={onIsKnownActualWeightCheckboxClick}
            value={values.isKnownRetreaderTiresActualWeight}
            wrapperClassName={styles.actionsCheckbox}
          />
        </div>

        <div className={styles.dialogRow}>
          <div className={styles.dialogCol6}>
            <label
              className={styles.formSectionTitle}
              htmlFor="collection-info-section"
            >
              {t(
                "transactions.grid.createRetreadingTransaction.form.collectionInfo"
              )}
              <CollectionInfoTooltip materials={materials} />
            </label>
            <MaterialsTable
              id="collection-info-section"
              keyPrefix="collectionInfoMaterials"
              label={t("categories.Tires", { unit: MaterialUnit.Units })}
              inputColLabel={t(
                `transactions.grid.createRetreadingTransaction.form.${
                  values.isKnownRetreaderTiresActualWeight ? "weight" : "units"
                }`
              )}
              estimatedColLabel={t(
                `transactions.grid.createRetreadingTransaction.form.${
                  values.isKnownRetreaderTiresActualWeight
                    ? "estimatedUnits"
                    : "estimatedWeight"
                }`
              )}
              materials={materials}
              errors={errors}
              values={values.collectionInfoMaterials}
              estimatedValues={collectionEstimationValues}
              siblingValidation={collectionMaterialsErrors}
              updateValue={updateValue}
              onBlur={(materialCode) => {
                validateValue(`collectionInfoMaterials.${materialCode}`);
                validateValue(`retreadRepairInfoMaterials.${materialCode}`);
              }}
            />
          </div>
          <div className={styles.dialogCol6}>
            <label
              className={styles.formSectionTitle}
              htmlFor="retread-info-section"
            >
              {t(
                "transactions.grid.createRetreadingTransaction.form.retreadNRepair"
              )}
            </label>
            <MaterialsTable
              id="retread-info-section"
              keyPrefix="retreadRepairInfoMaterials"
              label={t("categories.Tires", { unit: MaterialUnit.Kg })}
              inputColLabel={t(
                `transactions.grid.createRetreadingTransaction.form.weight`
              )}
              estimatedColLabel={t(
                `transactions.grid.createRetreadingTransaction.form.estimatedUnits`
              )}
              materials={materials}
              errors={errors}
              values={values.retreadRepairInfoMaterials || ""}
              siblingValidation={retreadRepairMaterialsErrors}
              estimatedValues={retreadingEstimationUnits}
              updateValue={updateValue}
              onBlur={(materialCode) => {
                validateValue(`collectionInfoMaterials.${materialCode}`);
                validateValue(`retreadRepairInfoMaterials.${materialCode}`);
              }}
            />
          </div>
        </div>
        <div className={styles.dialogCol6}>
          <RecoveryMaterialsTable
            values={values.recoveryInfoMaterials}
            errors={errors}
            recoveryGain={recoveryGain}
            updateValue={updateValue}
            validateValue={validateValue}
          />
        </div>

        <div id="supportingDocuments">
          <h3 className={classnames(styles.formSectionTitle, styles.required)}>
            {t("transactions.grid.createRetreadingTransaction.form.documents")}
          </h3>
          <FormControlMessage
            id="attachments"
            className={styles.error}
            errorText={errors.attachments}
            isError={
              !!errors.attachments &&
              errors.attachments !== `"attachments" is not allowed`
            }
          />
          <DraftFileUploader
            files={values.attachments}
            path="transactions/attachment"
            onChange={(files) => updateValue("attachments", files, true)}
          />
        </div>

        <div className={styles.dialogCol6}>
          <DatePicker
            id="production-report-date"
            isNoOffset
            inputClassName={styles.formField}
            title={t(
              `transactions.grid.createRetreadingTransaction.form.productionReportDate`
            )}
            placeholder={t(
              `transactions.grid.createRetreadingTransaction.form.productionReportDatePlaceholder`
            )}
            startDate={getDateObject(values.productionReportDate as string)}
            setStartDate={(v) => {
              updateValue("productionReportDate", getDateString(v), true);
            }}
            isError={!!errors.productionReportDate}
            errorText={errors.productionReportDate}
            valueFormat="MMMM dd, yyyy"
            maxDate={maxProductionReportDateRef.current}
          />
        </div>
        <TextInput
          id="retreading-transaction-comment"
          title={t(
            "transactions.grid.createRetreadingTransaction.form.comments"
          )}
          isMultiline
          isError={!!errors.comment}
          errorText={errors.comment}
          value={values.comment}
          onBlur={() => validateValue("comment")}
          onChange={(event, value) => updateValue("comment", value)}
          labelClassName={styles.formSectionTitle}
        />
      </div>
    </Dialog>
  );
};

export default RetreadingTransactionFormDialog;
